import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import {keyframes, useMediaQuery, useTheme } from '@mui/material';
import { formatDuration } from "../../utlis/generalUtils";
import servicesTranslations from '../../constants/servicesTrasnlations.json';
import { useTranslation } from 'react-i18next';
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import {CheckCircle} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";


function StatusIndicator({ icon, label, color, onClick }) {
    const getColor = () => {
        switch (color) {
            case "success":
                return "#10b981";
            case "info":
                return "#02b0c7";
            case "error":
                return "#ef4444";
            default:
                return "#6b7280";
        }
    };

    return (
        <Box
            onClick={onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: onClick ? "pointer" : "default"
            }}
        >
            <Avatar
                sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: getColor(),
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                }}
            >
                {typeof icon === "string" ? icon : icon}
            </Avatar>
            <Typography variant="body2" fontWeight="medium" color="text.primary">
                {label}
            </Typography>
            <Box
                sx={{
                    width: 48,
                    height: 4,
                    backgroundColor: getColor(),
                    borderRadius: 2,
                }}
            />
        </Box>
    );
}

StatusIndicator.propTypes = {
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node
    ]),
    label: PropTypes.string,
    color: PropTypes.string,
    onClick: PropTypes.func
};
const StepperStatusComponent = ({ stepsData, handleRerunService, handleSkipService }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [initialTimes, setInitialTimes] = useState({});
    const { t, i18n } = useTranslation(['cardItem']);
    const currentLang = i18n.language;
    const direction = i18n.dir();

    useEffect(() => {
        const timesMap = {};
        stepsData.forEach(step => {
            if (step?.service_name && step?.service_approximative_time != null) {
                timesMap[step.service_name] = step.service_approximative_time;
            }
        });
        setInitialTimes(timesMap);
    }, [stepsData]);

    const displaySteps = direction === 'rtl' && !isMobile
        ? [...stepsData].reverse()
        : stepsData;

    const getColor = (status) => {
        switch (status) {
            case "COMPLETED":
                return "success";
            case "IN_PROGRESS":
                return "info";
            case "ERROR":
                return "error";
            default:
                return "default";
        }
    };

    const bounce = keyframes`
        0%, 100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-3px);
        }
    `;
    return (
        <Box
            sx={{
                px: 3,
                py: 2,
                backgroundColor: "white",
                direction: direction,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: direction === "rtl" ? "row-reverse" : "row",
                    justifyContent: { xs: "center", sm: "center" },
                    gap: 4,
                    flexWrap: "wrap",
                    minWidth: "100%",
                }}
            >
                {displaySteps.map((step, index) => {
                    const label = servicesTranslations[currentLang][step.service_name] || step.service_name;
                    const color = getColor(step.service_status);

                    return (
                        <Box key={step.service_name} sx={{ display: "flex", flexDirection: "column" }}>
                            <StatusIndicator
                                icon={
                                    step.service_status === "ERROR" ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                animation: `${bounce} 1s infinite`,
                                                fontWeight: "bold",
                                                fontSize: 18,
                                                position: "relative",
                                            }}
                                        >
                                            !
                                        </Box>
                                    ) : step.service_status === "PENDING" ? (
                                        "..."
                                    ) : step.service_status === "IN_PROGRESS" ? (
                                        <CircularProgress
                                            size={13}
                                            thickness={3}
                                            style={{
                                                color: "#ffffff",
                                                position: "absolute",
                                            }}
                                        />
                                    ) : (
                                        <CheckCircle sx={{ fontSize: 16 }} />
                                    )
                                }
                                color={color}
                                size="large"
                                onClick={() => {
                                    if (step?.service_status === "ERROR") {
                                        handleRerunService({ service_name: step.service_name });
                                    }
                                }}
                            />
                            <Typography
                                variant="caption"
                                fontWeight="medium"
                                sx={{ mt: 1, maxWidth: "fit-content" }}
                            >
                                {label}
                                {initialTimes[step.service_name] != null &&
                                    ` (~${formatDuration(initialTimes[step.service_name])})`}
                            </Typography>
                            {step?.service_status === "ERROR"  && step?.service_name !== "OCR" && (
                                <Typography
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSkipService({ service_name: step.service_name });
                                    }}
                                    sx={{
                                        mt: 0.5,
                                        fontSize: "12px",
                                        color: "#0684a1",
                                        cursor: "pointer",
                                        textAlign: "center",
                                    }}
                                >
                                    {t('clickHereToSkip')}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

StepperStatusComponent.propTypes = {
    handleRerunService: PropTypes.func,
    handleSkipService: PropTypes.func,
    stepsData: PropTypes.arrayOf(
        PropTypes.shape({
            service_name: PropTypes.string.isRequired,
            service_status: PropTypes.oneOf(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'ERROR']).isRequired,
            service_approximative_time: PropTypes.number.isRequired,
        })
    ).isRequired,
};

export default StepperStatusComponent;